import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Users, BarChart3, Calendar, Shield } from 'lucide-react'
import { SimpleIcon } from '@adthlete/common'
import { initiateTikTokConnection } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

const TikTokConnect = () => {
  const navigate = useNavigate()
  const [isConnecting, setIsConnecting] = useState(false)
  const { athlete } = useAuth()

  const hasPendingTikTokAuth = !!athlete?.authTokenInfo?.some(
    (auth: any) => auth.platform === 'tiktok',
  )
  const hasConnectedTikTok = !!athlete?.socialAccounts?.some(
    (account: any) => account.platform === 'tiktok',
  )

  const handleConnect = async () => {
    if (hasPendingTikTokAuth && !hasConnectedTikTok) {
      navigate('/tiktok/auth-steps?resume=1')
      return
    }

    setIsConnecting(true)
    try {
      await initiateTikTokConnection()
    } catch (error) {
      console.error('Failed to initiate TikTok connection:', error)
      setIsConnecting(false)
    }
  }

  const benefits = [
    {
      icon: <Users className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Creator Partnerships',
      description: 'Connect with brands looking for TikTok creators in your niche',
    },
    {
      icon: <BarChart3 className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Performance Tracking',
      description: 'Monitor your TikTok video performance and engagement rates',
    },
    {
      icon: <Calendar className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Content Planning',
      description: 'Plan and schedule TikTok content for brand collaborations',
    },
    {
      icon: <Shield className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Secure Integration',
      description: 'Your TikTok data is protected with enterprise-grade security',
    },
  ]

  const permissions = [
    'Read your basic TikTok account information',
    'Access your TikTok profile details',
    'View your TikTok video statistics',
    'Access follower and engagement metrics',
  ]

  return (
    <div className="w-full max-w-4xl mx-auto pb-4">
      <div className="mb-4 md:mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings?tab=social')}
          className="text-gray-400"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">Connect TikTok</h1>
          <p className="text-gray-400">Link your TikTok account to get started</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Main Connect Card */}
        <Card variant="dark" className="overflow-hidden">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-black p-4 rounded-full">
                <SimpleIcon slug="tiktok" color="white" height={32} width={32} />
              </div>
            </div>
            <CardTitle className="text-white text-xl">Connect Your TikTok</CardTitle>
            <CardDescription className="text-gray-400">
              Connect your TikTok account to unlock powerful collaboration tools
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-white font-medium mb-3">What we'll access (read-only):</h3>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-3">
                <p className="text-blue-300 text-sm font-medium mb-1">
                  🔒 Performance Analytics Only
                </p>
                <p className="text-blue-200 text-xs">
                  We only access post performance metrics and analytics - we never post to your
                  account or make any changes.
                </p>
              </div>
              <ul className="space-y-2">
                {permissions.map((permission, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 bg-[#C0FD72] rounded-full mt-2 shrink-0" />
                    {permission}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 border-t border-white/10">
              <Button
                onClick={handleConnect}
                disabled={isConnecting}
                variant="lime"
                className="w-full font-medium"
                size="lg"
              >
                {isConnecting ? 'Connecting...' : 'Connect TikTok Account'}
              </Button>

              <p className="text-xs text-gray-400 text-center mt-3">
                You'll be redirected to TikTok to authorize the read-only connection
              </p>
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mt-3">
                <p className="text-green-300 text-xs text-center font-medium">
                  ✨ Trusted by thousands of creators worldwide via our partner site Talent Pitch
                  Pro
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Card */}
        <Card variant="dark" className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-white">Why Connect TikTok?</CardTitle>
            <CardDescription className="text-gray-400">
              Unlock these powerful features when you connect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-3">
                  <div className="shrink-0">{benefit.icon}</div>
                  <div>
                    <h4 className="text-white font-medium text-sm">{benefit.title}</h4>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Notice */}
      <Card className="mt-6 bg-white/5 backdrop-blur-xs border border-white/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-[#C0FD72] shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">Your Privacy is Protected</h4>
              <p className="text-gray-400 text-sm mb-2">
                We only access the minimum permissions required to provide our services with
                <strong className="text-white"> read-only access</strong> - we never post to your
                account or make any changes. You can disconnect your account at any time from your
                settings page.
              </p>
              <p className="text-[#C0FD72] text-sm font-medium">
                Join thousands of creators who trust us with their data via our partner site Talent
                Pitch Pro.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default TikTokConnect
